import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { validateUsername } from "@/api/users";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Check, X } from "lucide-react";

interface UsernameInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  placeholder?: string;
  onValidationChange?: (isValid: boolean) => void;
}

export function UsernameInput({
  value,
  onChange,
  disabled = false,
  required = false,
  className = "",
  label = "Username",
  placeholder = "Enter your username",
  onValidationChange
}: UsernameInputProps) {
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [validationMessage, setValidationMessage] = useState<string>("");
  const { toast } = useToast();

  const handleValidate = async () => {
    if (!value.trim()) {
      toast({
        title: "Username Required",
        description: "Please enter a username to validate.",
        variant: "destructive"
      });
      return;
    }

    setIsValidating(true);
    setValidationStatus('idle');
    setValidationMessage("");

    try {
      const result = await validateUsername(value.trim());
      
      if (result.isValid) {
        setValidationStatus('valid');
        setValidationMessage("Username is available!");
        toast({
          title: "Username Available",
          description: "This username is available and can be used.",
        });
        onValidationChange?.(true);
      } else {
        setValidationStatus('invalid');
        setValidationMessage(result.message || "Username is not available.");
        toast({
          title: "Username Not Available",
          description: result.message || "This username is already taken or invalid.",
          variant: "destructive"
        });
        onValidationChange?.(false);
      }
    } catch (error: any) {
      setValidationStatus('invalid');
      setValidationMessage("Failed to validate username. Please try again.");
      toast({
        title: "Validation Error",
        description: "Failed to validate username. Please try again.",
        variant: "destructive"
      });
      onValidationChange?.(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Reset validation status when input changes
    if (validationStatus !== 'idle') {
      setValidationStatus('idle');
      setValidationMessage("");
      onValidationChange?.(false);
    }
  };

  const getValidationIcon = () => {
    switch (validationStatus) {
      case 'valid':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'invalid':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getInputClassName = () => {
    const baseClassName = "h-12 border-slate-200 focus:border-orange-400 focus:ring-orange-400/20 focus:ring-2 focus:outline-none focus-visible:ring-orange-400/20 focus-visible:ring-2 focus-visible:outline-none rounded-lg space-grotesk bg-white/80 backdrop-blur-sm transition-all pr-10";
    
    switch (validationStatus) {
      case 'valid':
        return `${baseClassName} border-green-500 focus:border-green-500 focus:ring-green-500/20`;
      case 'invalid':
        return `${baseClassName} border-red-500 focus:border-red-500 focus:ring-red-500/20`;
      default:
        return baseClassName;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="username" className="text-slate-700 font-medium space-grotesk">
        {label}
      </Label>
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center">
        <div className="relative flex-1">
          <Input
            id="username"
            type="text"
            value={value}
            onChange={handleInputChange}
            required={required}
            disabled={disabled || isValidating}
            autoComplete="username"
            className={getInputClassName()}
            placeholder={placeholder}
          />
          {validationStatus !== 'idle' && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {getValidationIcon()}
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleValidate}
          disabled={disabled || isValidating || !value.trim()}
          className="w-full sm:w-auto h-10 sm:h-12 px-4 sm:px-6 sm:flex-shrink-0 sm:min-w-[100px] text-sm sm:self-center"
        >
          {isValidating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <span className="sm:hidden">Validate Username</span>
          <span className="hidden sm:inline">Validate</span>
        </Button>
      </div>
      {validationMessage && (
        <p className={`text-sm ${validationStatus === 'valid' ? 'text-green-600' : 'text-red-600'}`}>
          {validationMessage}
        </p>
      )}
    </div>
  );
}
